"use client"

import * as React from "react"
import { motion, type HTMLMotionProps } from "motion/react"

import { useIsInView, type UseIsInViewOptions } from "@/hooks/use-is-in-view"
import { mergeRefs } from "@/lib/utils"
import {
	Slot,
	type SlotProps,
	type WithAsChild,
} from "@/components/animate-ui/primitives/animate/slot"

type FadeProps = WithAsChild<
	{
		children?: React.ReactNode
		delay?: number
		initialOpacity?: number
		opacity?: number
		ref?: React.Ref<HTMLDivElement>
	} & UseIsInViewOptions &
		HTMLMotionProps<"div">
>

function Fade({
	ref,
	transition = { type: "spring", stiffness: 200, damping: 20 },
	delay = 0,
	inView = false,
	inViewMargin = "0px",
	inViewOnce = true,
	initialOpacity = 0,
	opacity = 1,
	asChild = false,
	...props
}: FadeProps) {
	const { ref: localRef, isInView } = useIsInView<HTMLDivElement>({
		inView,
		inViewOnce,
		inViewMargin,
	})

	const mergedRef = mergeRefs(ref, localRef)

	const variants = {
		hidden: { opacity: initialOpacity },
		visible: { opacity },
	}

	const combinedTransition = {
		...transition,
		// Convert delay from milliseconds to seconds for motion/react
		delay: (transition?.delay ?? 0) + delay / 1000,
	}

	if (asChild) {
		return (
			<Slot
				{...(props as SlotProps)}
				ref={mergedRef}
				initial="hidden"
				animate={isInView ? "visible" : "hidden"}
				exit="hidden"
				variants={variants}
				transition={combinedTransition}
			/>
		)
	}

	return (
		<motion.div
			{...props}
			ref={mergedRef}
			initial="hidden"
			animate={isInView ? "visible" : "hidden"}
			exit="hidden"
			variants={variants}
			transition={combinedTransition}
		/>
	)
}

type FadeListProps = Omit<FadeProps, "children"> & {
	children: React.ReactElement | React.ReactElement[]
	holdDelay?: number
}

function FadeList({
	children,
	delay = 0,
	holdDelay = 0,
	...props
}: FadeListProps) {
	const array = React.Children.toArray(children) as React.ReactElement[]

	return (
		<>
			{array.map((child, index) => (
				<Fade
					key={child.key ?? index}
					delay={delay + index * holdDelay}
					{...props}
				>
					{child}
				</Fade>
			))}
		</>
	)
}

export { Fade, FadeList, type FadeProps, type FadeListProps }
